import React from 'react';
import { IconType } from 'react-icons';
import { clsx } from 'clsx';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'ghost' | 'link';
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  icon?: IconType;
  iconPosition?: 'left' | 'right';
  loading?: boolean;
  fullWidth?: boolean;
}

const LoadingSpinner: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={clsx('animate-spin', className)}
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
  >
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
  </svg>
);

const variantConfig = {
  primary: {
    base: { backgroundColor: 'var(--theme-button-primary)', color: 'var(--theme-button-primary-text)' },
    hover: { backgroundColor: 'var(--theme-button-primary-hover)', boxShadow: '0 4px 14px rgba(139,92,246,0.25)' },
  },
  secondary: {
    base: { backgroundColor: 'var(--theme-button-secondary)', color: 'var(--theme-button-secondary-text)', border: '1px solid var(--theme-border)' },
    hover: { backgroundColor: 'var(--theme-button-secondary-hover)' },
  },
  ghost: {
    base: { backgroundColor: 'transparent', color: 'var(--theme-text)' },
    hover: { backgroundColor: 'var(--theme-button-ghost-hover)' },
  },
  success: {
    base: { backgroundColor: 'var(--theme-success-color)', color: '#ffffff' },
    hover: { backgroundColor: 'var(--theme-success-color)', opacity: 0.9 },
  },
  warning: {
    base: { backgroundColor: 'var(--theme-warning-color)', color: '#ffffff' },
    hover: { backgroundColor: 'var(--theme-warning-color)', opacity: 0.9 },
  },
  error: {
    base: { backgroundColor: 'var(--theme-error-color)', color: '#ffffff' },
    hover: { backgroundColor: 'var(--theme-error-color)', opacity: 0.9 },
  },
  link: {
    base: { backgroundColor: 'transparent', color: 'var(--theme-primary)' },
    hover: { color: 'var(--theme-button-primary-hover)', textDecoration: 'underline' as const },
  },
} as const;

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ variant = 'primary', size = 'md', icon: Icon, iconPosition = 'left', loading = false, fullWidth = false, className, children, disabled, style, ...props }, ref) => {
    const [isHovered, setIsHovered] = React.useState(false);

    const config = variantConfig[variant];
    const computedStyle = {
      ...config.base,
      ...(isHovered && !disabled && !loading ? config.hover : {}),
      transition: 'all var(--transition-fast)',
      ...style,
    };

    return (
      <button
        ref={ref}
        className={clsx(
          'inline-flex items-center justify-center rounded-lg font-medium',
          'focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-purple-500/20 focus-visible:ring-offset-1',
          'disabled:pointer-events-none disabled:opacity-50',
          'active:scale-[0.98] transition-transform',
          {
            'h-7 px-2 text-xs gap-1': size === 'xs',
            'h-8 px-3 text-sm gap-1.5': size === 'sm',
            'h-9 px-4 text-sm gap-2': size === 'md',
            'h-10 px-6 text-base gap-2': size === 'lg',
            'h-12 px-8 text-lg gap-2': size === 'xl',
          },
          { 'w-full': fullWidth },
          className
        )}
        style={computedStyle}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        disabled={disabled || loading}
        {...props}
      >
        {loading && <LoadingSpinner className="h-4 w-4" />}
        {Icon && !loading && iconPosition === 'left' && <Icon className="h-4 w-4" />}
        {children}
        {Icon && !loading && iconPosition === 'right' && <Icon className="ml-1 h-4 w-4" />}
      </button>
    );
  }
);

Button.displayName = 'Button';
